import type { BroadcastData } from './broadcast';
import type { PublicRolePermissionInfo, Reaction, UserData } from './user-info';
import { PersonalPermission } from './user-info';

export enum ArtifactStatus {
    SUCCEEDED = 'SUCCEEDED',
    IN_PROGRESS = 'IN_PROGRESS',
    FAILED = 'FAILED',
}

export enum ConnectionType {
    WAITING_ROOM = 'WAITING_ROOM',
}

export enum MediaPlatform {
    GOLOOM = 'GOLOOM',
}

export enum PeerType {
    USER = 'USER',
    MEETING_ROOM = 'MEETING_ROOM',
    INCOMING_PHONE_CALL = 'INCOMING_PHONE_CALL',
}

export enum AccessLevel {
    PUBLIC = 'PUBLIC',
    ORGANIZATION = 'ORGANIZATION',
    ADMINS = 'ADMINS',
}

export enum SummarizationMailRecipient {
    PARTICIPANTS_FROM_ORGANIZATION = 'PARTICIPANTS_FROM_ORGANIZATION',
    ADMINS_FROM_ORGANIZATION = 'ADMINS_FROM_ORGANIZATION',
}

export type CloudRecordingStatus = 'STARTING' | 'RUNNING' | 'TURNED_OFF';
export type SummarizationStatus = 'STARTING' | 'RUNNING' | 'TURNED_OFF';

export interface ConferencesHistoryItem {
    session_id: string;
    short_url_id: string;
    meeting_name?: string;
    started_at: string;
    ended_at: string;
    summarization_status?: ArtifactStatus;
    cloud_recording_status?: ArtifactStatus;
    has_meeting_report: boolean;
    has_local_recording: boolean;
}

export interface RoomConferenceState {
    local_recording_allowed: true;
    chat_allowed: boolean;
    control_allowed: boolean;
    chat_path?: string;
    broadcast_allowed: boolean;
    broadcast_feature_enabled: boolean;
    broadcast_data?: BroadcastData;
    access_restriction_organization_allowed: boolean;
    access_level: AccessLevel;
    waiting_room_enabled?: boolean;
    cloud_recording_status?: CloudRecordingStatus;
    summarization_status?: SummarizationStatus;
    summarization_mail_recipient?: SummarizationMailRecipient;
    meeting_name?: string;
}

export interface PermissionsInfo {
    version: number;
    public_role_permissions: PublicRolePermissionInfo[];
    personal_allowed: PersonalPermission[];
}

export interface PeerInfo {
    peer_id: string;
    peer_type: PeerType;
    version: number;
    state: PeerState;
    public_permissions_override?: Record<string, boolean> | null;
}

export interface PeerState {
    is_local_recording?: boolean;
    user_data: UserData;
    client_state?: {
        is_local_recording?: boolean;
        placeholder: {
            view: string;
            color: string;
            abbreviation?: string;
            avatarColor?: string;
        };
    };
    reaction?: Reaction;
}

export interface ConferenceConnectionData {
    media_session_id: string;
    peer_id: string;
    peer_token: string;
    room_id: string;
    uri: string;
    ws_uri: string;
    client_configuration?: {
        state_check_interval_seconds?: number;

        // Отдается только для встречи через Goloom
        media_server_url?: string;
        service_name?: string;
        ice_servers?: RTCIceServer[];
        goloom_session_open_ms?: number;
        conference_check_access_interval_ms?: number;
        waiting_room_peers_refresh_interval_ms?: number;
        reactions_available?: boolean;
        cloud_recording_available?: boolean;
        summarization_available?: boolean;
        extended_role_permissions_enabled?: boolean;
        report_problem_button_available?: boolean;
        new_grid?: boolean;
        calendar_summarization_receive_available?: boolean;
        alice_pro_enabled?: boolean;
    };
    connection_type?: ConnectionType;
    conference_state?: RoomConferenceState;
    media_platform: MediaPlatform;

    // Отдается только для встречи через Goloom
    credentials?: string;
    waiting_room_available?: boolean;
    conference_limit?: number;
    is_legal_entity?: boolean;
    organization_id?: string;
    session_id?: string;
    permissions?: PermissionsInfo;
    peers?: PeerInfo[];
    error?: string;
    message?: string;
    start_event_time?: number;
    show_user_mismatches_organization?: boolean;
    noise_cancellation_feature_enabled?: boolean;
}
